import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolWarning,
} from "../types";

/**
 * Cetrorelix — the first injection of a cycle most patients give themselves.
 *
 * The triggers the coach already guides are given once, at an hour someone else
 * chose, at the end of a stimulation. This is the opposite: 0.25 mg every
 * morning from around day five until the trigger, roughly eight to ten times,
 * and for many patients it is the first needle they have ever held.
 *
 * ### What is new here
 *
 * It arrives as a kit rather than a dose: a powder vial, a prefilled syringe of
 * sterile water, a 20G needle to mix with and a 27G needle to inject with. So
 * the needle change that urinary hCG introduced happens here too — and the
 * reconstitution carries a warning the other vials do not, because the molecule
 * is a peptide: shaking denatures it. A patient who shakes the vial injects a
 * full dose of something that no longer works, and nothing about the solution
 * looks wrong afterwards. That sentence is the reason this step is its own step.
 *
 * ### What is deliberately not here
 *
 * Nothing about the 3 mg single dose. The content names it in the dosage range
 * and describes only the daily 0.25 mg procedure, so that is the only procedure
 * restated — a protocol may omit, never invent.
 *
 * ### The sharps line
 *
 * Every other procedure in the catalogue ends by saying what to do with what is
 * left. This one is the only kit — a 20G needle, a 27G needle and a glass vial,
 * on a table, eight to ten mornings running — and it was the only one that said
 * nothing. The sentence was added to the approved content first, on the model
 * of the others, at Anaïs's instruction (2026-09-22).
 *
 * ### Why "use immediately" is copy here and a warning in the trigger
 *
 * Urinary hCG carries it as a `⚠` segment, so it is a `ProtocolWarning` with
 * that segment quoted. The antagonist content states the same clock in the prose
 * of its last step instead. A warning needs a `⚠` segment to cite, so this one
 * stays what the content made it — a sentence — and it is moved to the step that
 * starts the clock rather than the step that ends the procedure. A patient who
 * reads it at the end has already waited.
 *
 * Every sentence below comes from the vial procedure in
 * `content/data/medications/gnrh-antagonists.json`, and
 * `antagonist-protocol.test.ts` holds it there sentence by sentence.
 */

const CETROTIDE_BRANDS = [
  {
    name: "Cetrotide",
    markets: [
      "EU",
      "GB",
      "IS",
      "LI",
      "NO",
      "CH",
      "US",
      "CA",
      "AU",
      "NZ",
      "JP",
      "ZA",
      "SG",
      "ID",
      "MY",
      "PH",
    ],
  },
  { name: "Cetrorelix RBX", markets: ["AU"] },
  { name: "LHix", markets: ["PH"] },
  { name: "Asporelix", markets: ["MY"] },
] as const satisfies readonly ProtocolBrand[];

/**
 * Two hours, which is the only number in this procedure that decides anything.
 *
 * A daily antagonist works by never letting the pituitary off the hook. The
 * content draws the line at two hours and sends her to the clinic past it, so
 * this is said on the first step — before she has mixed anything, while the
 * answer can still change what she does.
 */
const MISSED_DOSE: ProtocolWarning = {
  id: "cetrotide.missed-dose",
  severity: "caution",
  // `none`, and the sentence explains why: it is the warning itself that tells
  // her when to call, not the coach routing her. A caution never pauses the
  // walk, so an escalation declared on one would promise a destination the
  // session has no moment to reach — see `protocol-contract.test.ts`.
  escalation: "none",
  copy: {
    en: "If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic.",
    fr: "Si le retard est inférieur à 2 heures, injectez immédiatement. Au-delà, contactez votre centre.",
  },
  sourceSegment:
    "⚠ Missed dose - If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic.",
};

const WHEN_TO_CALL: ProtocolWarning = {
  id: "cetrotide.when-to-call",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Call your clinic for severe site reactions (spreading redness, warmth, pus) or unusual symptoms.",
    fr: "Contactez votre centre en cas de réaction sévère au point d'injection (rougeur qui s'étend, chaleur, écoulement purulent) ou de symptômes inhabituels.",
  },
  sourceSegment:
    "⚠ When to call - Call your clinic for severe site reactions (spreading redness, warmth, pus) or unusual symptoms.",
};

/**
 * The half of "when to call" that does not belong to a clinic.
 *
 * The content listed a spreading site reaction, an allergic reaction and
 * "unusual symptoms" in one sentence with one destination. Anaphylaxis is not a
 * phone call to a fertility clinic (Anaïs, 2026-09-22), so the content was
 * split before this restated it, and the two halves route where each belongs.
 */
const BREATHING_OR_ALLERGIC: ProtocolWarning = {
  id: "cetrotide.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
    fr: "Aide urgente - Des difficultés respiratoires ou des signes de réaction allergique nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
};

/**
 * The two situations the triggers already covered and these did not.
 *
 * Not because they are likelier here — a spilled dose is likelier here, on a
 * kit she mixes herself eight to ten mornings running — but because a patient
 * who has one of them should not find out whether the app has an answer by
 * having one (Anaïs, 2026-09-22). The same situation gets the same action
 * whichever medication she is holding; four procedures disagreeing about that
 * is the disagreement worth removing.
 *
 * Bloating matters most here of all. These are given through exactly the
 * stimulation days when OHSS develops, and until now the coach only mentioned
 * it on the trigger walk — days later, to the same patient.
 */
const SPILL_OR_INCOMPLETE_DOSE: ProtocolWarning = {
  id: "cetrotide.spill-or-incomplete-dose",
  severity: "stop",
  escalation: "clinic-soon",
  copy: {
    en: "Spill or incomplete dose - If you spill any medication or suspect an incomplete dose, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
    fr: "Renversement ou dose incomplète - Si vous renversez du produit ou suspectez une dose incomplète, appelez votre centre dès que possible. Ne tentez pas une seconde injection sans avis médical.",
  },
  sourceSegment:
    "⚠ Spill or incomplete dose - If you spill any medication or suspect an incomplete dose, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
};

const SEVERE_BLOATING: ProtocolWarning = {
  id: "cetrotide.severe-bloating",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Severe bloating - Call your clinic the same day for severe bloating.",
    fr: "Ballonnements sévères - Appelez votre centre le jour même en cas de ballonnements sévères.",
  },
  sourceSegment:
    "⚠ Severe bloating - Call your clinic the same day for severe bloating.",
};

export const CETROTIDE_PROTOCOL: InjectionProtocol = {
  medicationSlug: "cetrotide",
  brands: CETROTIDE_BRANDS,
  // From the content's own alias list. Recognition only: a record saying
  // "Cetrorelix" does not say which kit is on her table.
  aliases: ["Cetrorelix", "Cétrorélix", "Cétrotide"],
  indication: "antagonist",
  // The walk restates the 0.25 mg daily procedure only — see the file comment —
  // so that is the one strength it can place. A record of the 3 mg single
  // dose is asked about rather than walked.
  dose: { unit: "mg", strengths: [0.25] },
  form: "vial",
  prescriptionForms: ["lyophilized_powder"],
  route: "subcutaneous",
  version: "2026-09-22.1",
  derivedFrom: "content/data/medications/gnrh-antagonists.json#cetrotide",
  steps: [
    {
      id: "cetrotide.vial.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash hands and open the {brand} kit. It contains a powder vial, a prefilled syringe of sterile water, a yellow mixing needle (20G), and a grey injection needle (27G). Clean the powder vial stopper with an alcohol swab.",
        fr: "Préparer - Lavez-vous les mains et ouvrez le kit {brand}. Il contient un flacon de poudre, une seringue préremplie d'eau stérile, une aiguille de reconstitution jaune (20 G) et une aiguille d'injection grise (27 G). Désinfectez le bouchon du flacon de poudre à l'alcool.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "cetrotide.vial.reconstitute",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Reconstitute dose - Attach the yellow mixing needle to the prefilled water syringe. Push all the sterile water into the powder vial, aiming the stream at the glass wall. Swirl gently in a circular motion until fully dissolved - do NOT shake, as shaking denatures the peptide and reduces effectiveness. The solution should be clear and bubble-free; do not use if cloudy or particles are visible. Without removing the needle, invert the vial and draw the entire solution back into the syringe. Use immediately after mixing - do not store reconstituted {brand}.",
        fr: "Reconstituer la dose - Fixez l'aiguille jaune sur la seringue d'eau préremplie. Injectez toute l'eau stérile dans le flacon de poudre en visant la paroi. Faites tourner doucement en mouvement circulaire jusqu'à dissolution complète - NE PAS agiter, l'agitation dénature le peptide et réduit l'efficacité. La solution doit être claire et sans bulles ; ne pas utiliser si trouble ou particulaire. Sans retirer l'aiguille, retournez le flacon et aspirez toute la solution dans la seringue. À utiliser immédiatement après préparation - ne pas conserver de {brand} reconstitué.",
      },
    },
    {
      id: "cetrotide.vial.change-the-needle",
      // Confirmed, not narrated. Two needles with two purposes come in one
      // pack, and injecting through the 20G mixing needle is painful, plausible
      // and entirely silent — nothing about the act tells her it happened.
      // `MEDICATIONS.md` names this as the step of this kit that must be
      // asserted rather than read. It is also where she is holding the syringe
      // and can still check what is on the end of it.
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Prepare injection - Remove the yellow mixing needle and attach the grey injection needle (27G). Tap the syringe to move any air bubbles to the top and push them out gently.",
        fr: "Préparer l'injection - Retirez l'aiguille jaune et fixez l'aiguille d'injection grise (27 G). Tapotez la seringue pour faire remonter les bulles d'air et chassez-les doucement.",
      },
    },
    {
      id: "cetrotide.vial.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean site - Clean the injection site on your lower abdomen (1-2 inches from the navel) with an alcohol swab and let dry.",
        fr: "Désinfecter le site - Désinfectez le site sur le bas du ventre (à 2-5 cm du nombril) à l'alcool et laissez sécher.",
      },
    },
    {
      id: "cetrotide.vial.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Pinch the skin, insert the needle at 90°, inject slowly and steadily, hold for 5 seconds, then withdraw. Inject at the same time each morning and continue through trigger day.",
        fr: "Injecter - Pincez la peau, insérez l'aiguille à 90°, injectez lentement et régulièrement, maintenez 5 secondes, puis retirez. Injectez à la même heure chaque matin et continuez jusqu'au jour du déclencheur inclus.",
      },
      warnings: [
        WHEN_TO_CALL,
        BREATHING_OR_ALLERGIC,
        SPILL_OR_INCOMPLETE_DOSE,
        SEVERE_BLOATING,
      ],
    },
    {
      id: "cetrotide.vial.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Apply gentle pressure - do not rub. Alternate sides daily. Dispose of both needles and the vial in a sharps container.",
        fr: "Après l'injection - Appliquez une légère pression - sans frotter. Alternez les côtés chaque jour. Jetez les deux aiguilles et le flacon dans un collecteur.",
      },
    },
  ],
};
